import {
  Avatar,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  SwipeableDrawer,
  Typography,
} from '@material-ui/core';
import { AddCircleOutline, Close, ExitToApp } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router';

import userSvg from 'assets/user.svg';

import { ConfirmationPopper } from '@components/ConfirmationPopper';
import { Link } from '@components/Link';
import { Spacer } from '@components/Spacer';
import { Spinner } from '@components/Spinner';
import { messages } from '@core/messages';
import { IAuthContext } from '@providers/auth';
import {
  homeUrl,
  offerCreateUrl,
  userOffersActiveUrl,
  userOffersExpiredUrl,
} from '@urls';
import useEnv, { ENV } from '@utils/env';
import { getTestAttribute } from '@utils/misc';

import { useOffersCountLazyQuery } from './queries.rest';

import useStyles from './styles';

interface IProps {
  open: boolean;
  authContext: IAuthContext;
  toggleOpen(open: boolean): void;
}

export const SideBar: React.FC<IProps> = ({ authContext: { user, logout }, open, toggleOpen }) => {
  const {
    location: { pathname },
    push,
  } = useHistory();
  const [getOffersCountQuery, { data, loading }] = useOffersCountLazyQuery();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const env = useEnv();
  const displayName =
    user?.fullName?.trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    'Użytkowniku';

  const handleLogOut = () => {
    toggleOpen(false);
    setTimeout(() => {
      push(homeUrl);
      logout();
      enqueueSnackbar(messages.logout);
    }, 200);
  };

  useEffect(() => {
    if (open) {
      getOffersCountQuery();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      toggleOpen(false);
    }
  }, [pathname]);

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={() => toggleOpen(false)}
      onOpen={() => toggleOpen(true)}
      classes={{ paper: classes.drawer }}
    >
      <List className={classes.root}>
        <ListItem disableGutters className={classes.actions}>
          <Button
            color="primary"
            component={Link}
            startIcon={<AddCircleOutline />}
            to={offerCreateUrl}
            variant="outlined"
          >
            Opublikuj ofertę
          </Button>
          <IconButton onClick={() => toggleOpen(false)}>
            <Close />
          </IconButton>
        </ListItem>
        <ListItem>
          <Spacer height={10} />
        </ListItem>
        <ListItem disableGutters>
          <img src={userSvg} alt="User" className={classes.userIcon} />
          <Typography color="textPrimary">Witaj, {displayName}</Typography>
        </ListItem>

        <Divider className={classes.divider} />

        {!data && loading ? (
          <Spinner big />
        ) : (
          <>
            <ListItem disableGutters component={Link} to={userOffersActiveUrl}>
              <Avatar className={classes.indicator1}>
                <Typography>{data?.me.activeOffers}</Typography>
              </Avatar>
              <Typography color="textPrimary">Aktywne oferty</Typography>
            </ListItem>
            <ListItem disableGutters component={Link} to={userOffersExpiredUrl}>
              <Avatar className={classes.indicator2}>
                <Typography>{data?.me.expiredOffers}</Typography>
              </Avatar>
              <Typography color="textPrimary">Nieaktywne oferty</Typography>
            </ListItem>
          </>
        )}

        <Divider className={classes.divider} />

        <ListItem disableGutters>
          <ConfirmationPopper onSuccess={handleLogOut}>
            <Button
              {...getTestAttribute('btn', 'logoutSidebar')}
              fullWidth
              color="primary"
              variant="outlined"
              startIcon={<ExitToApp color="primary" />}
            >
              <Typography>Wyloguj</Typography>
            </Button>
          </ConfirmationPopper>
        </ListItem>
      </List>
    </SwipeableDrawer>
  );
};
